import React from "react";
import { Card } from "reactstrap";
import { Icon } from "../../components/Component";
import { Skeleton, Spin, Tag } from "antd";

interface LedgerEntry {
  id: number;
  transaction: string;
  dateRange: string;
  amountIn: number;
  amountInText: string;
  amountOut: number;
  userTotalIn: number;
  userTotalOut: number;
  userApprovedTotalIn: number;
  userApprovedTotalOut: number;
  status: "DRAFT" | "APPROVED" | "PENDING" | "REJECTED";
  expenseCount?: number;
}

interface LedgerSummaryProps {
  entries?: LedgerEntry[];
  balance?: number;
  onView?: (entry: LedgerEntry) => void;
  loading?: boolean;
  lastEntryRef?: React.RefObject<HTMLDivElement>;
  endOfList?: boolean;
}

const LedgerSummary: React.FC<LedgerSummaryProps> = ({
  entries = [],
  balance = 0,
  onView,
  loading = false,
  lastEntryRef,
  endOfList = false
}) => {
  const currentBalance = balance;

  // Interactive Cards: Handle card click for viewing details
  const handleView = (entry: LedgerEntry) => {
    if (onView) {
      onView(entry);
    }
  };

  // Ledger Display: Render individual ledger entry card
  const renderLedgerCard = (entry: LedgerEntry, index: number) => (
    <div 
      key={entry.id} 
      ref={index === entries.length - 1 ? lastEntryRef : null}
    >
      <Card 
        className="mb-3" 
        style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
      >
        <div className="d-flex align-items-start p-3">
        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="mb-2">
                <span
                  className="inlineTableCellLink"
                  style={{
                    display: "inline-block",
                    verticalAlign: "middle",
                    cursor: "pointer",
                    color: "#2557d6",
                    fontWeight: "600",
                    fontSize: "16px"
                  }}
                  onClick={() => handleView(entry)}
                  title={`${entry.id}. ${entry.transaction}`}
                >
                  {entry.id}. {entry.transaction}
                </span>

              </div>
              <div className="row">
                <div className="col-md-3">
                  <div className="text-muted small">Date Range:</div>
                  <div className="fw-bold" style={{ fontSize: '13px', fontFamily: 'Inter, Roboto, sans-serif' }}>{entry.dateRange}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Amount In:</div>
                  <div className="fw-bold" style={{ fontSize: '13px', fontFamily: 'Inter, Roboto, sans-serif' }}>
                    {entry.userApprovedTotalIn > 0 ? `₹${entry.userApprovedTotalIn.toLocaleString()}.00` : '₹0.00'}
                    {entry.expenseCount && entry.expenseCount > 0 && (
                      <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '4px' }}>
                        ({entry.expenseCount} {entry.expenseCount === 1 ? 'expense' : 'expenses'})
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Amount Out:</div>
                  <div className="fw-bold" style={{ fontSize: '13px', fontFamily: 'Inter, Roboto, sans-serif' }}>
                    {entry.userApprovedTotalOut > 0 ? `₹${entry.userApprovedTotalOut.toLocaleString()}.00` : '₹0.00'}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Status:</div>
                  <div>
                    <Tag 
                      color={
                        entry.status.toLowerCase().includes("draft") ? "geekblue" :
                        entry.status.toLowerCase().includes("pending") ? "gold" :
                        entry.status.toLowerCase().includes("approved") ? "green" : 
                        entry.status.toLowerCase().includes("rejected") ? "red" : "default"
                      } 
                      style={{ fontSize: 14, padding: '2px 12px' }}
                    >
                      {entry.status.charAt(0).toUpperCase() + entry.status.slice(1).toLowerCase()}
                    </Tag>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </Card>
    </div>
  );

  return (
    <div className="ledger-summary">
      {/* Ledger Entries List */}
      <div 
        style={{
          maxHeight: '600px',
          overflowY: 'auto',
          paddingRight: '8px'
        }}
        className="custom-scrollbar"
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <Spin size="large" tip="Loading ledger entries..." />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No ledger entries found</p>
          </div>
        ) : (
          <>
            {entries.map((entry, index) => renderLedgerCard(entry, index))}
            {loading && (
              <div className="mb-3">
                <Card style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <div className="p-3">
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </div>
                </Card>
              </div>
            )}
            {endOfList && entries.length > 0 && (
              <div className="text-center py-3">
                <p className="text-muted small">No more entries to load</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LedgerSummary;
